import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Send, X } from 'lucide-react';
import { inputCls } from './FormKit';
import { SettingsCard } from './SettingsKit';
import { fetchJSON } from '../../utils/api';

// Preview & Send card of a reminder mail (Overdue Rules & Reminder, Training
// Follow-up Reminder). Reflects the SAVED settings (the server rebuilds from
// the DB) and today's data: the recipients, the exact mail one of them would
// get, and Send now.
//   recipientsUrl / renderUrl(key) — the mail's endpoints; recipientsUrl null
//                    = nothing to preview
//   describe(r)    — the count label of one recipient row
//   send           — { url, body, covers } for the Send now confirm
//   head           — extra controls rendered above the recipient list
//   enabledKey     — the recipients envelope's master-switch field
//   disabledNote   — shown when the mail's master switch is off
//   emptyNote      — shown when nobody would be mailed
//   unmatched*     — the amber box of people who cannot be emailed: its
//                    title(n), its explanation and its columns [{key,label}]
const DEFAULT_UNMATCHED_COLUMNS = [
  { key: 'clientName', label: 'Client' },
  { key: 'role', label: 'Role' },
  { key: 'name', label: 'Name' },
  { key: 'reason', label: 'Why' },
];
const defaultUnmatchedTitle = (n) => `${n} lead${n === 1 ? '' : 's'} on listed items cannot be emailed`;

const MailPreview = ({
  refreshKey, recipientsUrl, renderUrl, describe, send, head, enabledKey, disabledNote, emptyNote,
  unmatchedNote, unmatchedTitle = defaultUnmatchedTitle, unmatchedColumns = DEFAULT_UNMATCHED_COLUMNS,
}) => {
  const [recipients, setRecipients] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [selected, setSelected] = useState('');
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sendMsg, setSendMsg] = useState('');

  const loadRecipients = useCallback(async () => {
    if (!recipientsUrl) { setRecipients([]); setUnmatched([]); setSelected(''); setHtml(''); setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const data = await fetchJSON(recipientsUrl);
      setRecipients(data.recipients || []);
      setUnmatched(data.unmatched || []);
      setEnabled(data[enabledKey] !== false);
      if (data.recipients && data.recipients.length > 0) setSelected(data.recipients[0].key);
      else { setSelected(''); setHtml(''); }
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [recipientsUrl, enabledKey]);

  // Reload on mount, after each successful save (refreshKey bumps) and when
  // the previewed mail changes, so the preview — recipients included —
  // reflects the newly saved settings.
  useEffect(() => { loadRecipients(); }, [loadRecipients, refreshKey]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(renderUrl(selected));
        if (!cancelled) setHtml(data.html || '');
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, [selected, renderUrl]);

  const label = useMemo(() => {
    const r = recipients.find(x => x.key === selected);
    if (!r) return '';
    const kind = r.type === 'summary' ? 'Summary' : 'Individual';
    return `${kind} — ${r.name} <${r.email}> · ${describe(r)}`;
  }, [recipients, selected, describe]);

  const handleSent = (msg) => {
    setConfirmOpen(false);
    setSendMsg(msg);
    setTimeout(() => setSendMsg(''), 5000);
  };

  return (
    <>
      <SettingsCard
        title="Preview & Send"
        hint={<>Reflects <span className="font-medium">saved</span> settings and today's data. Only people who would actually receive an email are listed.{!enabled && ` ${disabledNote}`}</>}
        aside={(
          <>
            {sendMsg && <span className="text-xs text-green-600">{sendMsg}</span>}
            <button onClick={loadRecipients} className="text-xs text-blue-600 hover:text-blue-800">Refresh</button>
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={recipients.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Send className="w-3.5 h-3.5" />
              Send Now
            </button>
          </>
        )}
      >
      <div className="p-5 space-y-3">
        {head}
        {err && <div className="text-sm text-red-600">{err}</div>}
        {/* Someone who cannot be emailed is never skipped silently — list who
            and why, so the fix (link the lead to a staff record / add the
            email on /qm/staff) is obvious. */}
        {!loading && unmatched.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm font-medium text-amber-800">
              {unmatchedTitle(unmatched.length)}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">{unmatchedNote}</p>
            <div className="mt-2 overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-amber-700">
                    {unmatchedColumns.map((c, i) => (
                      <th key={c.key} className={`${i < unmatchedColumns.length - 1 ? 'pr-4 ' : ''}py-1 font-semibold`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {unmatched.map((u, i) => (
                    <tr key={i} className="border-t border-amber-100 text-amber-900">
                      {unmatchedColumns.map((c, j) => (
                        <td key={c.key} className={`${j < unmatchedColumns.length - 1 ? 'pr-4 ' : ''}py-1`}>{u[c.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {loading ? (
          <p className="text-sm text-slate-500">Loading recipients…</p>
        ) : recipients.length === 0 ? (
          <p className="text-sm text-slate-500">{emptyNote}</p>
        ) : (
          <>
            <select value={selected} onChange={e => setSelected(e.target.value)} className={inputCls}>
              {recipients.map(r => (
                <option key={r.key} value={r.key}>
                  {r.type === 'summary' ? '[Summary] ' : '[Individual] '}{r.name} — {describe(r)}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500">{label}</p>
            <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
              <iframe title="email-preview" srcDoc={html} className="w-full" style={{ height: 520, border: 0, background: '#fff' }} />
            </div>
          </>
        )}
      </div>
      </SettingsCard>
      {confirmOpen && (
        <SendNowModal
          recipientCount={recipients.length}
          send={send}
          onClose={() => setConfirmOpen(false)}
          onSent={handleSent}
        />
      )}
    </>
  );
};

// In-app confirm for the "Send now" button (matches the app's modal pattern).
export const SendNowModal = ({ recipientCount, send, onClose, onSent }) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const confirmSend = async () => {
    setSending(true); setError(null);
    try {
      const data = await fetchJSON(send.url, {
        method: 'POST',
        body: JSON.stringify(send.body || {}),
      });
      onSent(data.skipped
        ? (data.reason === 'no recipients' ? 'Nobody to notify.' : `Skipped (${data.reason}).`)
        : `Sent ${data.sent}/${data.recipientCount} email(s).`);
    } catch (e) { setError(e.message); setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Send Now</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Send this email to <span className="font-semibold">{recipientCount}</span> recipient(s) now?
          </p>
          <p className="text-sm text-slate-700">
            Covers: <span className="font-semibold">{send.covers}</span>
          </p>
          <p className="text-xs text-slate-500">Uses the saved settings and current data, and sends even if this reminder is disabled.</p>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={sending} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded disabled:opacity-50">Cancel</button>
            <button onClick={confirmSend} disabled={sending} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              <Send className="w-4 h-4" />{sending ? 'Sending…' : 'Send Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MailPreview;
